'use client';
import React from 'react';
import { usePagination, useHitsPerPage } from 'react-instantsearch';

import {
  Pagination as CommonPagination,
  PaginationProps as CommonPaginationProps,
} from '@workspace/ui/common/Pagination/Pagination';
import { itemsPerPageOptions } from '@workspace/constants';

import type { UsePaginationProps, UseHitsPerPageProps } from 'react-instantsearch';

export type PaginationProps = UsePaginationProps &
  Partial<UseHitsPerPageProps> & {
    className?: string;
    variant?: 'jumper' | 'default';
    showItemsPerPageSelector?: boolean;
    defaultItemsPerPage?: number;
  } & Partial<Pick<CommonPaginationProps, 'onPageChange' | 'onItemsPerPageChange' | 'onScrollToTop'>>;

export function AlgoliaPagination({
  padding,
  totalPages,
  variant = 'jumper',
  items: hitsPerPageItems,
  transformItems,
  onPageChange,
  onItemsPerPageChange,
  defaultItemsPerPage = 12,
  ...props
}: PaginationProps) {
  const { currentRefinement, nbPages, refine } = usePagination(
    { padding, totalPages },
    {
      $$widgetType: 'ais.pagination',
    },
  );

  const { items: hitsPerPageOptionsItems, refine: refineHitsPerPage } = useHitsPerPage(
    {
      items:
        hitsPerPageItems ||
        itemsPerPageOptions.map((option) => ({
          label: option.label,
          value: option.value,
          default: option.value === defaultItemsPerPage,
        })),
      transformItems,
    },
    { $$widgetType: 'ais.hitsPerPage' },
  );

  const currentHitsPerPage = hitsPerPageOptionsItems.find((item) => item.isRefined)?.value || defaultItemsPerPage;

  const handlePageChange = (page: number) => {
    // Convert from 1-based (common pagination) to 0-based (Algolia)
    refine(page - 1);
    onPageChange?.(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    refineHitsPerPage(itemsPerPage);
    onItemsPerPageChange?.(itemsPerPage);
  };

  return (
    <CommonPagination
      currentPage={currentRefinement + 1} // Convert from 0-based to 1-based
      totalPages={nbPages}
      itemsPerPage={currentHitsPerPage}
      onPageChange={handlePageChange}
      onItemsPerPageChange={handleItemsPerPageChange}
      navigationControlsClassNames={{
        container: 'w-full md:w-auto justify-center md:justify-start',
        pageNumbers: 'max-w-[calc(100%-88px)] overflow-x-auto p-1 gap-3xs text-black',
      }}
      variant={variant}
      {...props}
    />
  );
}
